import type { ErrorEvent, EventHint } from '@sentry/react';

// F5-13 (roadmap de produccion, 2026-08-13): filtro preventivo de datos sensibles
// antes de enviar un evento a Sentry (tercero externo). Hoy Sentry esta inerte
// (VITE_SENTRY_DSN sin configurar, ver R-24), pero logger.error() en produccion
// ya adjunta como "extra" cualquier argumento extra que reciba -- sin este filtro,
// el dia que se active el DSN, cualquiera de los cientos de logger.error(msg, {...})
// existentes (o futuros) podria filtrar tokens, contraseñas u otros datos sensibles
// a un servicio externo sin que nadie lo note.
//
// Estrategia: redactar por NOMBRE de campo (no por tipo de dato), de forma
// recursiva, en request.headers/cookies, extra, contexts y breadcrumbs -- los
// lugares donde puede aparecer contexto arbitrario adjuntado por la app.

const SENSITIVE_KEY_PATTERN =
  /token|password|contrase|secret|authorization|api[-_]?key|credential|cookie/i;

function scrubValue(value: unknown, key?: string): unknown {
  if (key && SENSITIVE_KEY_PATTERN.test(key)) {
    return '[Filtered]';
  }

  if (Array.isArray(value)) {
    return value.map((item) => scrubValue(item));
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [k, scrubValue(v, k)]),
    );
  }

  return value;
}

export function scrubSentryEvent(event: ErrorEvent, _hint: EventHint): ErrorEvent {
  if (event.request) {
    if (event.request.headers) {
      event.request.headers = scrubValue(event.request.headers) as Record<string, string>;
    }
    if (event.request.cookies) {
      event.request.cookies = '[Filtered]' as unknown as Record<string, string>;
    }
  }

  if (event.extra) {
    event.extra = scrubValue(event.extra) as typeof event.extra;
  }

  if (event.contexts) {
    event.contexts = scrubValue(event.contexts) as typeof event.contexts;
  }

  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => ({
      ...breadcrumb,
      data: breadcrumb.data ? (scrubValue(breadcrumb.data) as Record<string, unknown>) : breadcrumb.data,
    }));
  }

  return event;
}
